// Hook compartido para fetch de /api/public/landing-stats con cache global +
// abort en unmount. Las secciones de la landing lo usan para mostrar números
// reales (precios scrapeados, tiendas monitoreadas, último scrape).

import { useEffect, useState } from 'react';
import { API_BASE_URL } from '@/lib/utils/constants';

export interface LandingStats {
  competitorPrices: number;
  scrapesLastWeek: number;
  activeStores: number;
  trackedProducts: number;
  /** Ya no viaja (Fase 2: no se muestra a qué hora trabaja la plataforma). */
  lastScrapeAt?: string | null;
}

// Vault 346 (L0): la misma base que el resto de la app (relativa por omisión), sin la IP de la VM.
const API_BASE = API_BASE_URL;

let cache: { value: LandingStats; expiresAt: number } | null = null;
let inflight: Promise<LandingStats | null> | null = null;
const CLIENT_TTL_MS = 60_000;

async function fetchOnce(): Promise<LandingStats | null> {
  if (cache && cache.expiresAt > Date.now()) return cache.value;
  if (inflight) return inflight;
  inflight = fetch(`${API_BASE}/api/public/landing-stats`)
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      if (j?.success && j?.data) {
        cache = { value: j.data as LandingStats, expiresAt: Date.now() + CLIENT_TTL_MS };
        return cache.value;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function useLandingStats(): { stats: LandingStats | null; loading: boolean } {
  const [stats, setStats] = useState<LandingStats | null>(
    cache && cache.expiresAt > Date.now() ? cache.value : null
  );
  const [loading, setLoading] = useState(!stats);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchOnce().then((s) => {
      if (cancelled) return;
      if (s) setStats(s);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return { stats, loading };
}
